import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  admin,
  type AdminQResponseRow,
  type AdminQuestionnaireSummary,
} from '../../api/admin'
import { ApiError } from '../../api/client'
import { InviteModal } from './InviteModal'

export function AdminQuestionnaires() {
  const [params, setParams] = useSearchParams()
  const code = params.get('code') ?? ''
  const [catalog, setCatalog] = useState<AdminQuestionnaireSummary[] | null>(null)
  const [responses, setResponses] = useState<AdminQResponseRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [inviteCode, setInviteCode] = useState<string | null>(null)
  const [pseudonymize, setPseudonymize] = useState(false)

  useEffect(() => {
    admin.questionnaires()
      .then((r) => setCatalog(r.items))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Error de red.'))
  }, [])

  useEffect(() => {
    admin.questionnaireResponses(code || undefined)
      .then((r) => setResponses(r.items))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Error de red.'))
  }, [code])

  return (
    <section>
      <div className="page-header">
        <h1>Cuestionarios</h1>
        <div className="flex items-center gap-sm">
          <Link to="/admin/questionnaire-editor" className="btn no-underline">
            Ver preguntas
          </Link>
          <label className="flex items-center gap-xs text-xs text-text-muted">
            <input
              type="checkbox"
              checked={pseudonymize}
              onChange={(e) => setPseudonymize(e.target.checked)}
            />
            Pseudonimizar
          </label>
          <a
            href={admin.exportDatasetUrl({
              ...(code ? { code } : {}),
              ...(pseudonymize ? { pseudonymize: true } : {}),
            })}
            className="btn no-underline"
            download
          >
            Exportar CSV
          </a>
          <a
            href={admin.identityMapUrl()}
            className="btn-ghost no-underline text-xs"
            download
            title="Mapeo pseudonym ↔ identidad — descargar una vez y custodiar cifrado fuera del sistema (protocolo §7.2)"
          >
            Identity map
          </a>
          <Link to="/admin/invitations" className="btn no-underline">
            Invitaciones →
          </Link>
          {catalog && catalog.length > 0 && (
            <button
              type="button"
              className="btn-primary"
              onClick={() =>
                setInviteCode(code || catalog[0]?.code || '')
              }
            >
              + Invitar
            </button>
          )}
        </div>
      </div>
      {error && <div className="banner-err mb-md">{error}</div>}

      {/*
        Qué cuentan los mosaicos: respuestas de los instrumentos ASIGNABLES
        (los que se invitan por ola). El chequeo previo de cada sesión y los
        cuestionarios propios no tienen mosaico — sí aparecen en la tabla de
        abajo, que lista todas las respuestas sin filtrar. Sin esta línea, un
        "0" se leía como "el cuestionario no funciona" (pregunta de mg,
        2026-09-08).
      */}
      <p className="text-sm text-text-muted">
        Cada mosaico cuenta las respuestas guardadas de un instrumento asignable
        (SMTQ, CSAI-2R), sumando todas las olas y todos los clubes. El chequeo
        previo de cada sesión y los cuestionarios propios no tienen mosaico:
        aparecen igual en la tabla de abajo, y todas las preguntas se ven en{' '}
        <Link to="/admin/questionnaire-editor">Ver preguntas</Link>.
      </p>

      <div className="grid grid-cols-2 gap-md sm:grid-cols-3">
        {catalog?.map((c) => (
          <button
            key={c.code}
            type="button"
            onClick={() => {
              const next = new URLSearchParams(params)
              if (code === c.code) next.delete('code')
              else next.set('code', c.code)
              setParams(next)
            }}
            className={[
              'card text-left transition-colors',
              code === c.code ? 'border-primary ring-1 ring-primary/40' : 'hover:border-primary/50',
            ].join(' ')}
          >
            <div className="text-xs uppercase tracking-wide text-text-muted">
              {c.name_es}
            </div>
            <div className="mt-xs text-xl font-bold">{c.response_count}</div>
            <div className="mt-xs text-xs text-text-muted">{c.code}</div>
          </button>
        ))}
      </div>

      <h2>Respuestas {code && <span className="lowercase">({code})</span>}</h2>
      {inviteCode && catalog && (
        <InviteModal
          defaultCode={inviteCode}
          codes={catalog.map((c) => ({ code: c.code, name_es: c.name_es }))}
          onClose={() => setInviteCode(null)}
        />
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Cuándo</th>
              <th>Cuestionario</th>
              <th>Usuario</th>
              <th>Email</th>
              <th>Puntajes</th>
              <th className="text-right">Ver</th>
            </tr>
          </thead>
          <tbody>
            {responses === null && (
              <tr>
                <td colSpan={6} className="text-text-muted">
                  Cargando…
                </td>
              </tr>
            )}
            {responses?.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-text-muted">
                  Sin respuestas todavía.
                </td>
              </tr>
            )}
            {responses?.map((r) => (
              <tr key={r.id}>
                <td>
                  {new Date(r.submitted_at).toLocaleString('es-AR', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}
                </td>
                <td>
                  <span className="tag">{r.code}</span>
                </td>
                <td>
                  <Link to={`/admin/users/${r.user_id}`}>
                    {r.submitted_name || r.username || `#${r.user_id}`}
                  </Link>
                </td>
                <td className="text-text-muted">{r.submitted_email || '—'}</td>
                <td className="text-xs text-text-muted">
                  {Object.entries(r.scores.subscales).map(([k, s], i) => (
                    <span key={k}>
                      {i > 0 && ' · '}
                      {s.label}:{' '}
                      <strong className="text-text-primary">{s.raw}</strong>
                    </span>
                  ))}
                  {r.scores.total && (
                    <>
                      {' '}· Total:{' '}
                      <strong className="text-text-primary">
                        {r.scores.total.raw}
                      </strong>
                    </>
                  )}
                </td>
                <td className="text-right">
                  <Link to={`/admin/questionnaires/${r.id}`}>detalle</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
